import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { Backdrop, Box, Button, CircularProgress, TextField } from "@mui/material";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import { backendPut } from "@accredo/fe-shared/services/dataService.js";

const EMPTY_FORM = { iban: "", bic: "", account_holder: "", bank_name: "" };

/**
 * Ändert die Kontodaten (IBAN/BIC/Kontoinhaber/Bank) eines bestehenden
 * Bankkontos - für sysadmin im Kundendetail-Bankkonten-Tab und für
 * kd_verwaltung auf der eigenen Bankkonten-Seite (ACC-323-Folgeticket).
 * Die Kundenzuordnung selbst bleibt unangetastet (dafür BankAccountKundeModal.jsx).
 */
function EditBankAccountDialog({ open, bankAccount, onClose, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }
    setForm({
      iban: bankAccount?.iban ?? "",
      bic: bankAccount?.bic ?? "",
      account_holder: bankAccount?.account_holder ?? "",
      bank_name: bankAccount?.bank_name ?? "",
    });
    setOpenCD(true);
    // eslint-disable-next-line
  }, [open, bankAccount?.id]);

  const handleFieldChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const isValid =
    form.iban.trim() &&
    form.bic.trim() &&
    form.account_holder.trim() &&
    form.bank_name.trim();

  const handleSave = async () => {
    await backendPut(`data/bank-accounts/${bankAccount.id}`, {
      label: "Kontodaten ändern",
      setIsLoading,
      enqueueSnackbar,
      toSend: form,
      onSuccess: () => {
        enqueueSnackbar("Kontodaten geändert", { variant: "success" });
        onSuccess?.();
        onClose();
      },
    });
  };

  useEffect(() => {
    setTca({
      title: "Kontodaten ändern",
      maxWidth: "sm",
      content: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="IBAN"
            value={form.iban}
            onChange={handleFieldChange("iban")}
            fullWidth
          />
          <TextField
            label="BIC"
            value={form.bic}
            onChange={handleFieldChange("bic")}
            fullWidth
          />
          <TextField
            label="Kontoinhaber"
            value={form.account_holder}
            onChange={handleFieldChange("account_holder")}
            fullWidth
          />
          <TextField
            label="Bank"
            value={form.bank_name}
            onChange={handleFieldChange("bank_name")}
            fullWidth
          />
        </Box>
      ),
      actions: (
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave} disabled={!isValid}>
            Speichern
          </Button>
        </>
      ),
    });
    // eslint-disable-next-line
  }, [form]);

  return (
    <>
      <Backdrop
        open={isLoading}
        style={{ zIndex: 1300, flexDirection: "column" }}
      >
        <CircularProgress style={{ marginBottom: 16 }} />
      </Backdrop>
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        tca={tca}
        onClose={onClose}
      />
    </>
  );
}

EditBankAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  bankAccount: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    iban: PropTypes.string,
    bic: PropTypes.string,
    account_holder: PropTypes.string,
    bank_name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default EditBankAccountDialog;
